import { callExternalApi } from "./external-api.service";
import { getAccessTokenSilentlyOutsideComponent } from "./workspace.service";
import Ably from "ably";
import Push from "ably/push"; // Push plugin must be imported from ably/push

const API_SERVER_URL = import.meta.env.VITE_API_SERVER_URL;

// Singleton Ably client for push notifications
let pushClient = null;

// Track push activation state to prevent double subscription
let isPushActivated = false;
let subscribedChannel = null;

/**
 * Get Ably token request for real-time updates
 * @param {string} audioFileUUID - Optional audio file UUID to subscribe to
 * @returns {Promise<Object>} Token request object
 */
export const getAblyToken = async (audioFileUUID = null) => {
  console.log("[AblyService] Getting token for audioFile:", audioFileUUID);

  const config = {
    url: `${API_SERVER_URL}/ably/token-request`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${await getAccessTokenSilentlyOutsideComponent()}`,
    },
    data: {
      audioFileUUID,
    },
  };

  const { data, error } = await callExternalApi({ config });

  if (error) {
    console.error("[AblyService] Failed to get token:", error);
    throw new Error(error.message || "Failed to get Ably token");
  }

  return data.tokenRequest;
};

// ==================== PUSH NOTIFICATION SUPPORT ====================

/**
 * Initialize Ably client with push notification support
 * @param {string} userUUID - User's UUID for client identification
 * @returns {Promise<Ably.Realtime | null>}
 */
export const initializeAblyWithPush = async (userUUID) => {
  console.log("[AblyService] Initializing Ably with push for user:", userUUID);

  // Return existing client if already initialized and connected
  if (pushClient && pushClient.connection.state === "connected") {
    console.log("[AblyService] Returning existing push client");
    return pushClient;
  }

  try {
    // Create Ably client with push support
    // Per Ably docs: pass pushServiceWorkerUrl and Push plugin
    pushClient = new Ably.Realtime({
      authCallback: async (tokenParams, callback) => {
        try {
          const tokenRequest = await getAblyToken();
          callback(null, tokenRequest);
        } catch (error) {
          callback(error, null);
        }
      },
      clientId: `user:${userUUID}`,
      pushServiceWorkerUrl: "/sw-push.js",
      plugins: { Push },
    });

    // Wait for connection
    await new Promise((resolve, reject) => {
      pushClient.connection.on("connected", () => {
        console.log("[AblyService] Push client connected");
        resolve();
      });
      pushClient.connection.on("failed", (err) => {
        console.error("[AblyService] Push client connection failed:", err);
        reject(err);
      });
      // Timeout after 10 seconds
      setTimeout(() => reject(new Error("Connection timeout")), 10000);
    });

    return pushClient;
  } catch (error) {
    console.error("[AblyService] Failed to initialize push client:", error);
    return null;
  }
};

/**
 * Activate push notifications for the current browser
 * Ably handles VAPID keys, PushManager subscription, and device registration automatically
 * @param {string} userUUID - User's UUID to subscribe to their notification channel
 * @returns {Promise<boolean>} Success status
 */
export const activatePush = async (userUUID) => {
  console.log(
    "[AblyService] Activating push notifications for user:",
    userUUID,
  );

  const channelName = `notifications:${userUUID}`;

  // Guard against duplicate activation for the same user
  if (isPushActivated && subscribedChannel === channelName) {
    console.log("[AblyService] Push already activated for this user, skipping");
    return true;
  }

  if (!pushClient) {
    console.error("[AblyService] Push client not initialized");
    return false;
  }

  try {
    // Ably's push.activate() handles everything:
    // 1. Generates/uses VAPID keys
    // 2. Subscribes via PushManager
    // 3. Registers device with Ably
    await pushClient.push.activate();
    console.log("[AblyService] Push activated successfully");

    // Subscribe to user's notification channel to receive push notifications
    const notificationChannel = pushClient.channels.get(channelName);
    await notificationChannel.push.subscribeClient();
    console.log(
      "[AblyService] Subscribed to notification channel:",
      channelName,
    );

    // Track successful activation
    isPushActivated = true;
    subscribedChannel = channelName;

    return true;
  } catch (error) {
    console.error("[AblyService] Failed to activate push:", error);
    return false;
  }
};

/**
 * Deactivate push notifications for the current browser
 * @returns {Promise<boolean>} Success status
 */
export const deactivatePush = async () => {
  console.log("[AblyService] Deactivating push notifications");

  if (!pushClient) {
    console.warn("[AblyService] Push client not initialized");
    // Reset state even if client is missing
    isPushActivated = false;
    subscribedChannel = null;
    return true;
  }

  try {
    await pushClient.push.deactivate();
    console.log("[AblyService] Push deactivated successfully");

    // Reset activation state
    isPushActivated = false;
    subscribedChannel = null;

    return true;
  } catch (error) {
    console.error("[AblyService] Failed to deactivate push:", error);
    return false;
  }
};

/**
 * Get the push notification state
 * @returns {string} Push state
 */
export const getPushState = () => {
  if (!pushClient || !pushClient.push) {
    return "NOT_INITIALIZED";
  }
  return pushClient.push.state || "UNKNOWN";
};

/**
 * Disconnect the push client
 */
export const disconnectPushClient = () => {
  if (pushClient) {
    console.log("[AblyService] Disconnecting push client");
    pushClient.close();
    pushClient = null;
  }
  // Reset activation state
  isPushActivated = false;
  subscribedChannel = null;
};
